import { useEffect, useMemo, useState } from "react";
import { api, apiError, fmtDate } from "./api";
import { Card, PageHead, Pill, Btn, Confirm, Textarea, Select } from "./widgets";
import { toast } from "sonner";
import { ExternalLink, History } from "lucide-react";

// Editable sections mapped onto CMS content documents.
const SECTIONS = [
  { key: "home", label: "Home", docs: ["lang:en", "lang:es"] },
  { key: "about", label: "About", docs: ["lang:en", "lang:es"] },
  { key: "servicesPage", label: "Services Overview", docs: ["lang:en", "lang:es"] },
  { key: "services", label: "Service Pages (01–05 active)", docs: ["services"] },
  { key: "resourcesPage", label: "Resources", docs: ["lang:en", "lang:es"] },
  { key: "blogPage", label: "Blog Page", docs: ["lang:en", "lang:es"] },
  { key: "faqPage", label: "FAQ", docs: ["lang:en", "lang:es"] },
  { key: "contactPage", label: "Contact", docs: ["lang:en", "lang:es"] },
  { key: "footer", label: "Footer", docs: ["lang:en", "lang:es"] },
  { key: "nav", label: "Navigation", docs: ["lang:en", "lang:es"] },
  { key: "authorPage", label: "Author Page", docs: ["lang:en", "lang:es"] },
  { key: "legal", label: "Legal Pages", docs: ["lang:en", "lang:es"] },
  { key: "common", label: "Shared / Common", docs: ["lang:en", "lang:es"] },
];

const flatten = (obj, prefix = "", out = []) => {
  if (obj === null || obj === undefined) return out;
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => flatten(v, `${prefix}${i}.`, out));
    return out;
  }
  if (typeof obj === "object") {
    Object.entries(obj).forEach(([k, v]) => flatten(v, `${prefix}${k}.`, out));
    return out;
  }
  if (typeof obj === "string" || typeof obj === "number") out.push({ path: prefix.slice(0, -1), value: String(obj) });
  return out;
};

const setPath = (obj, path, value) => {
  const segs = path.split(".");
  let cur = obj;
  for (let i = 0; i < segs.length - 1; i++) {
    const s = segs[i];
    const nextIsIndex = /^\d+$/.test(segs[i + 1]);
    if (cur[s] === undefined) cur[s] = nextIsIndex ? [] : {};
    cur = cur[s];
  }
  cur[segs[segs.length - 1]] = value;
};

const clone = (o) => JSON.parse(JSON.stringify(o));

const WebsiteEditor = () => {
  const [section, setSection] = useState(SECTIONS[0]);
  const [meta, setMeta] = useState({});
  const [docs, setDocs] = useState({}); // key -> full content doc
  const [edits, setEdits] = useState({}); // key -> working draft object
  const [dirty, setDirty] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [compare, setCompare] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const keys = ["lang:en", "lang:es", "services"];
      const results = await Promise.all(keys.map((k) => api.get(`/admin/content/${k}`)));
      const map = {};
      const ed = {};
      results.forEach((r) => {
        map[r.data.key] = r.data;
        ed[r.data.key] = clone(r.data.draft ?? r.data.published);
      });
      setDocs(map);
      setEdits(ed);
      setDirty(false);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => {
    load();
    api.get("/admin/content").then((r) => {
      const m = {};
      r.data.forEach((d) => (m[d.key] = d));
      setMeta(m);
    }).catch(() => {});
  }, []);

  const fields = useMemo(() => {
    const primary = edits[section.docs[0]];
    if (!primary) return [];
    const root = section.key === "services" ? primary : primary[section.key];
    return flatten(root, "", []);
  }, [edits, section]);

  const getVal = (docKey, path) => {
    const root = section.key === "services" ? edits[docKey] : edits[docKey]?.[section.key];
    if (!root) return "";
    return flatten(root, "", []).find((f) => f.path === path)?.value ?? "";
  };

  const setVal = (docKey, path, value) => {
    setEdits((prev) => {
      const next = { ...prev, [docKey]: clone(prev[docKey]) };
      if (section.key === "services") setPath(next[docKey], path, value);
      else setPath(next[docKey], `${section.key}.${path}`, value);
      return next;
    });
    setDirty(true);
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      for (const k of section.docs) await api.put(`/admin/content/${k}/draft`, { data: edits[k] });
      toast.success("Draft saved — not yet live.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    setBusy(true);
    try {
      for (const k of section.docs) {
        await api.put(`/admin/content/${k}/draft`, { data: edits[k] });
        await api.post(`/admin/content/${k}/publish`);
      }
      toast.success("Published — changes are now live.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setConfirmDiscard(false);
    for (const k of section.docs) await api.post(`/admin/content/${k}/discard`).catch(() => {});
    toast.success("Draft discarded.");
    load();
  };

  const openVersions = async () => {
    const { data } = await api.get(`/admin/content/${section.docs[0]}/versions`);
    setVersions(data);
    setShowVersions(true);
  };

  const restore = async (index) => {
    for (const k of section.docs) await api.post(`/admin/content/${k}/restore/${index}`).catch(() => {});
    setShowVersions(false);
    toast.success("Version restored into the draft. Publish to make it live.");
    load();
  };

  const openCompare = () => {
    const doc = docs[section.docs[0]];
    if (!doc || !doc.draft) {
      toast.info("No draft to compare — save a draft first.");
      return;
    }
    const pub = {};
    flatten(doc.published).forEach((f) => (pub[f.path] = f.value));
    const changes = flatten(doc.draft)
      .filter((f) => pub[f.path] !== undefined && pub[f.path] !== f.value)
      .map((f) => ({ path: f.path, from: pub[f.path], to: f.value }));
    setCompare(changes);
  };

  const lead = docs["lang:en"];
  const hasDraft = section.docs.some((k) => docs[k]?.draft);

  return (
    <div data-testid="website-editor">
      <PageHead
        title="Website Content"
        sub="Edit → Save Draft → Preview → Publish. The live site only changes on Publish."
        actions={
          <>
            <Btn onClick={openVersions}><History className="h-3.5 w-3.5" /> Versions</Btn>
            <Btn onClick={openCompare}>Compare</Btn>
            <a href="/?preview=draft" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-[3px] border border-[#171717]/20 px-4 py-2.5 text-[0.65rem] font-medium uppercase tracking-[0.16em] text-[#171717]/75 hover:border-[#C6A46A]">
              <ExternalLink className="h-3.5 w-3.5" /> Preview Draft
            </a>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <Card className="h-fit p-3">
          <div className="-m-1 flex gap-1 overflow-x-auto p-1 lg:block lg:space-y-0.5 lg:overflow-visible">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setSection(s)}
                data-testid={`cms-section-${s.key}`}
                className={`block min-h-[44px] w-full shrink-0 whitespace-nowrap rounded-[3px] px-3 py-2.5 text-left text-[0.72rem] uppercase tracking-[0.12em] transition-colors ${
                  section.key === s.key ? "bg-[#171717] text-[#C6A46A]" : "text-[#171717]/65 hover:bg-[#F4F0E8]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </Card>

        <div>
          <Card className="sticky top-16 z-20 mb-4 flex flex-wrap items-center justify-between gap-3 border-l-2 border-l-[#C6A46A]">
            <div className="text-xs text-[#171717]/60">
              <p>Last published: <span className="text-[#171717]">{fmtDate(lead?.publishedAt)}</span> {lead?.publishedBy && `by ${lead.publishedBy}`}</p>
              {hasDraft && (
                <p className="mt-1">
                  <Pill tone="champagne">Unpublished draft</Pill>{" "}
                  <span className="ml-1">{lead?.draftUpdatedBy && `last edit by ${lead.draftUpdatedBy}`}</span>
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Btn onClick={saveDraft} disabled={busy || !dirty} data-testid="cms-save-draft">Save Draft</Btn>
              {hasDraft && <Btn onClick={() => setConfirmDiscard(true)} data-testid="cms-discard">Discard Draft</Btn>}
              <Btn primary onClick={publish} disabled={busy} data-testid="cms-publish">Publish</Btn>
            </div>
          </Card>

          <Card>
            <p className="mb-5 text-[0.62rem] font-medium uppercase tracking-[0.18em] text-[#171717]/45">
              {section.label} — {fields.length} editable fields
            </p>
            <div className="space-y-5">
              {fields.map((f) => (
                <div key={f.path} className="border-b border-[#171717]/6 pb-5">
                  <p className="mb-2 font-mono text-[0.62rem] text-[#8a6f3e]">{section.key}.{f.path}</p>
                  {section.key === "services" ? (
                    <Textarea
                      rows={f.value.length > 90 ? 3 : 1}
                      value={f.value}
                      onChange={(e) => setVal("services", f.path, e.target.value)}
                      data-testid={`cms-field-${f.path}`}
                    />
                  ) : (
                    <div className="grid gap-3 md:grid-cols-2">
                      <Textarea
                        rows={f.value.length > 90 ? 3 : 1}
                        value={getVal("lang:en", f.path)}
                        onChange={(e) => setVal("lang:en", f.path, e.target.value)}
                        placeholder="English"
                        data-testid={`cms-en-${f.path}`}
                      />
                      <Textarea
                        rows={f.value.length > 90 ? 3 : 1}
                        value={getVal("lang:es", f.path)}
                        onChange={(e) => setVal("lang:es", f.path, e.target.value)}
                        placeholder="Español"
                        data-testid={`cms-es-${f.path}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {showVersions && (
        <div className="fixed inset-0 z-[80] flex items-end bg-[#171717]/60 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true">
          <div className="max-h-[85vh] w-full overflow-y-auto rounded-t-xl bg-white p-7 pb-[calc(1.75rem+env(safe-area-inset-bottom))] sm:max-h-[80vh] sm:max-w-lg sm:rounded-[3px] sm:pb-7">
            <h3 className="text-lg font-light text-[#171717]">Version history</h3>
            <div className="mt-5 space-y-3">
              {versions.length === 0 && <p className="text-sm text-[#171717]/50">No versions yet.</p>}
              {versions.map((v) => (
                <div key={v.index} className="flex items-center justify-between border border-[#171717]/10 px-4 py-3">
                  <div>
                    <p className="text-sm text-[#171717]">Version {v.index + 1} — {v.note}</p>
                    <p className="text-[0.65rem] text-[#171717]/50">{fmtDate(v.savedAt)} · {v.savedBy}</p>
                  </div>
                  <Btn onClick={() => restore(v.index)}>Restore</Btn>
                </div>
              ))}
            </div>
            <div className="mt-6 text-right"><Btn onClick={() => setShowVersions(false)}>Close</Btn></div>
          </div>
        </div>
      )}

      {compare && (
        <div className="fixed inset-0 z-[80] flex items-end bg-[#171717]/60 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true">
          <div className="max-h-[85vh] w-full overflow-y-auto rounded-t-xl bg-white p-7 pb-[calc(1.75rem+env(safe-area-inset-bottom))] sm:max-h-[80vh] sm:max-w-2xl sm:rounded-[3px] sm:pb-7">
            <h3 className="text-lg font-light text-[#171717]">Draft vs. published — {compare.length} change(s)</h3>
            <div className="mt-5 space-y-4">
              {compare.length === 0 && <p className="text-sm text-[#171717]/50">No differences.</p>}
              {compare.map((c) => (
                <div key={c.path} className="border border-[#171717]/10 p-4">
                  <p className="font-mono text-[0.62rem] text-[#8a6f3e]">{c.path}</p>
                  <p className="mt-2 text-xs text-red-700/80 line-through">{c.from}</p>
                  <p className="mt-1 text-xs text-emerald-700">{c.to}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 text-right"><Btn onClick={() => setCompare(null)}>Close</Btn></div>
          </div>
        </div>
      )}

      <Confirm
        open={confirmDiscard}
        title="Discard this draft?"
        body="All unpublished changes in this section will be lost. The published website is not affected."
        confirmLabel="Discard draft"
        onConfirm={discard}
        onCancel={() => setConfirmDiscard(false)}
      />
    </div>
  );
};

export default WebsiteEditor;
